// Lightweight analytics wrapper — fires GA4 gtag + Segment/Mixpanel drop-in
// All conversion-critical events go through gtag() so they appear in GA4 reports
// and can be used as goals in Google Ads.

const DEV = process.env.NODE_ENV !== 'production';

// GDPR consent gate — CookieConsentBanner.jsx persists the user's choice
// here. A single choke point in these four low-level functions covers every
// named event below without touching each call site individually.
function hasAnalyticsConsent() {
  try {
    const raw = window.localStorage.getItem('ip_cookie_consent');
    return raw ? JSON.parse(raw).analytics === true : false;
  } catch { return false; }
}

function gtag(...args) {
  if (!hasAnalyticsConsent()) return;
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

function track(event, props = {}) {
  if (DEV) console.log('[Analytics]', event, props);
  if (!hasAnalyticsConsent()) return;
  if (typeof window !== 'undefined' && window.analytics?.track) {
    window.analytics.track(event, { ...props, platform: 'web' });
  }
}

function identify(userId, traits = {}) {
  if (DEV) console.log('[Analytics] identify', userId, traits);
  if (!hasAnalyticsConsent()) return;
  if (typeof window !== 'undefined' && window.analytics?.identify) {
    window.analytics.identify(userId, traits);
  }
}

function page(name, props = {}) {
  if (DEV) console.log('[Analytics] page', name, props);
  if (!hasAnalyticsConsent()) return;
  if (typeof window !== 'undefined' && window.analytics?.page) {
    window.analytics.page(name, props);
  }
}

// ── Named events ──────────────────────────────────────────────────────────────
export const analytics = {
  track, identify, page,

  // User lifecycle
  userRegistered: (method = 'email') => {
    track('User Registered', { method });
    gtag('event', 'sign_up', { method });
  },
  emailVerified: () => track('Email Verified'),
  userLoggedIn:  () => {
    track('User Logged In');
    gtag('event', 'login');
  },

  // ── Subscription gate (key funnel event) ──────────────────────────────────
  subscriptionGateShown: (chapterNumber, roleSlug) => {
    track('Subscription Gate Shown', { chapterNumber, roleSlug });
    gtag('event', 'subscription_gate_shown', { chapter_number: chapterNumber, role_slug: roleSlug });
  },

  subscribeCTAClicked: (sourcePage, planType) => {
    track('Subscribe CTA Clicked', { sourcePage, planType });
    gtag('event', 'subscribe_cta_click', { source_page: sourcePage, plan_type: planType });
  },

  // ── Pricing & subscribe pages ─────────────────────────────────────────────
  pricingViewed: (source) => {
    track('Pricing Page Viewed', { source });
    gtag('event', 'pricing_page_view', { source });
  },

  subscribeViewed: (planType, roleSlug) => {
    track('Subscribe Page Viewed', { planType, roleSlug });
    gtag('event', 'subscribe_page_view', { plan_type: planType, role_slug: roleSlug });
  },

  subscribeStarted: (plan, role) => track('Subscription Started', { plan, role }),

  // ── Purchase (conversion) ─────────────────────────────────────────────────
  purchaseCompleted: (planType, priceUsd, couponUsed = false) => {
    track('Purchase Completed', { planType, priceUsd, couponUsed });
    gtag('event', 'purchase', {
      currency: 'USD',
      value: priceUsd,
      items: [{ item_id: planType, item_name: planType, price: priceUsd, quantity: 1 }],
    });
  },

  subscribeFailed: (error) => {
    track('Subscription Failed', { error });
    gtag('event', 'subscription_failed', { error_message: error });
  },

  // ── Quiz / session ────────────────────────────────────────────────────────
  sessionStarted:   (subChapterId, role, type) => track('Session Started', { subChapterId, role, type }),
  questionAnswered: (type, score, roleTitle)   => track('Question Answered', { type, score, roleTitle }),

  chapterCompleted: (chapterNumber, roleSlug, scorePct) => {
    track('Chapter Completed', { chapterNumber, roleSlug, scorePct });
    gtag('event', 'chapter_complete', {
      chapter_number: chapterNumber,
      role_slug: roleSlug,
      score_pct: scorePct,
    });
  },

  sessionCompleted: (score, total, roleTitle)  => track('Session Completed', { score, total, roleTitle }),
  feedbackViewed:   (score)                    => track('AI Feedback Viewed', { score }),

  // ── JD Prep ───────────────────────────────────────────────────────────────
  jdPrepGenerated: (hadResume) => {
    track('JD Prep Generated', { hadResume });
    gtag('event', 'jd_prep_generated', { had_resume: hadResume });
  },

  // ── Certificates ──────────────────────────────────────────────────────────
  certificateEarned: (role) => {
    track('Certificate Earned', { role });
    gtag('event', 'certificate_earned', { role_title: role });
  },

  // ── UI interactions ───────────────────────────────────────────────────────
  darkModeToggled: (theme)    => track('Dark Mode Toggled', { theme }),
  timerUsed:       (duration) => track('Quiz Timer Used', { duration }),
};

export default analytics;
